import { inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class AccessibilityObserverService {
  private document = inject(DOCUMENT);
  private observer!: MutationObserver;
  private landmarkCounter: number = 0;
  private started = false;

  constructor() {}

  public start(): void {
    if (this.started) return;

    this.started = true;
    const body = this.document.body as HTMLElement;

    this.applyFixesToTree(body);

    this.initObserver();
  }

  private applyFixesToTree(root: HTMLElement): void {
    this.applyWcagFixes(root);
    root.querySelectorAll('*').forEach((el) => {
      if (el instanceof HTMLElement) {
        this.applyWcagFixes(el);
      }
    });
  }

  private initObserver(): void {
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              this.applyWcagFixes(node);
              node.querySelectorAll('*').forEach((child) => {
                if (child instanceof HTMLElement) {
                  this.applyWcagFixes(child);
                }
              });
            }
          });
        } else if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement && mutation.attributeName) {
          this.handleAttributeMutation(mutation.target, mutation.attributeName);
        }
      }

      setTimeout(() => {
        this.fixEmptyOrGenericTableHeaders(this.document.body);
      }, 200);
    });

    this.observer.observe(this.document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-valuenow', 'aria-labelledby', 'aria-activedescendant'],
    });
  }

  private applyWcagFixes(element: HTMLElement): void {
    this.fixPrimeNgComponents(element);
    this.fixGenericInputs(element);
    this.fixDropdownRoles(element);
    this.fixAccessibleNames(element);
    this.fixInvalidAriaMultiselectable(element);
    this.fixUnlabeledInteractiveElements(element);
    this.fixNestedInteractiveControls(element);
    this.fixInvalidAriaRequired(element);
    this.fixEmptyOrGenericTableHeaders(element);
    this.fixMissingLandmarks(element);
    this.ensureLabelledbyReferencesExist(element);
    this.fixMultiselectWidgetRoles(element);
    this.fixMultiselectOptionNesting(element);
  }

  private handleAttributeMutation(target: HTMLElement, attr: string): void {
    switch (attr) {
      case 'aria-valuenow':
        this.fixInvalidAriaValues(target);
        break;

      case 'aria-labelledby':
        this.ensureLabelledbyReferencesExist(target);
        break;

      case 'aria-activedescendant':
        this.fixInvalidAriaActivedescendant(target);
        break;
    }
  }

  private fixMultiselectOptionNesting(el: HTMLElement): void {
    if (el.tagName === 'LI' && el.closest('ul[role="listbox"]')) {
      if (!el.hasAttribute('role')) el.setAttribute('role', 'option');

      const ariaChecked = el.getAttribute('aria-checked');
      const ariaSelected = el.getAttribute('aria-selected');

      if (ariaChecked !== null) {
        if (ariaSelected === null) {
          el.setAttribute('aria-selected', ariaChecked === 'true' ? 'true' : 'false');
        }
        el.removeAttribute('aria-checked');
      } else if (ariaSelected === null) {
        el.setAttribute('aria-selected', 'false');
      }

      const inputs = el.querySelectorAll('input.p-checkbox-input, input[type="checkbox"]');
      inputs.forEach((inp) => {
        const input = inp as HTMLInputElement;

        input.removeAttribute('aria-label');
        input.removeAttribute('aria-labelledby');
        if (input.id) input.removeAttribute('id');

        input.setAttribute('aria-hidden', 'true');
        input.setAttribute('role', 'presentation');

        input.setAttribute('tabindex', '-1');
        input.setAttribute('disabled', '');
        input.style.pointerEvents = 'none';
      });
    }
  }

  private fixMultiselectWidgetRoles(el: HTMLElement): void {
    if (el.classList.contains('p-multiselect')) {
      if (!el.hasAttribute('role')) el.setAttribute('role', 'combobox');
      if (!el.hasAttribute('aria-haspopup')) el.setAttribute('aria-haspopup', 'listbox');
      if (!el.hasAttribute('aria-expanded')) el.setAttribute('aria-expanded', 'false');
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', `Multiselect filter ${++this.landmarkCounter}`);
      }
    }

    if (el.tagName === 'UL' && !el.hasAttribute('role') && el.closest('.p-multiselect-panel')) {
      el.setAttribute('role', 'listbox');
      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        el.setAttribute('aria-label', 'Available options');
      }
    }
  }

  private ensureLabelledbyReferencesExist(el: HTMLElement): void {
    if (el.hasAttribute('aria-labelledby')) {
      const ids = (el.getAttribute('aria-labelledby') || '').trim().split(/\s+/).filter(Boolean);
      if (!ids.length) {
        el.removeAttribute('aria-labelledby');
        return;
      }
      const anyMissing = ids.some((id) => !this.document.getElementById(id));
      if (anyMissing) {
        const fallback = el.getAttribute('aria-label');
        if (!fallback) el.removeAttribute('aria-labelledby');
      }
    }
  }

  // PrimeNG component accessibility fixes
  private fixPrimeNgComponents(el: HTMLElement): void {
    // p-checkbox
    if (el.classList.contains('p-checkbox')) {
      const box = el.querySelector('.p-checkbox-box') as HTMLElement | null;
      const input = el.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
      const parentInteractive = el.closest(
        '[role="option"], [role="row"], [role="button"], [role="link"], [role="menuitem"]'
      );
      const isNestedInInteractive = !!parentInteractive && parentInteractive !== el;

      if (box) {
        if (!isNestedInInteractive) {
          if (!box.hasAttribute('role')) {
            box.setAttribute('role', 'checkbox');
          }

          if (!box.hasAttribute('tabindex')) {
            box.setAttribute('tabindex', '0');
          }

          if (!box.hasAttribute('aria-checked')) {
            box.setAttribute('aria-checked', input?.checked ? 'true' : 'false');
          }

          if (input) {
            const sync = () => box.setAttribute('aria-checked', input.checked ? 'true' : 'false');
            input.addEventListener('change', sync);
          }

          if (!box.hasAttribute('aria-label') && !box.hasAttribute('aria-labelledby')) {
            const label = input?.getAttribute('aria-label') || 'Checkbox option';
            box.setAttribute('aria-label', label);
          }
        } else {
          box.removeAttribute('role');
          box.removeAttribute('tabindex');
          box.removeAttribute('aria-checked');
          box.removeAttribute('aria-label');
          box.removeAttribute('aria-labelledby');
        }
      }
    }

    if (el.classList.contains('p-multiselect-list-container')) {
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      }

      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'region');
      }

      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        el.setAttribute('aria-label', 'Multiselect options list');
      }
    }

    //p-multislect
    if (el.classList.contains('p-multiselect')) {
      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'combobox');
      }

      if (!el.hasAttribute('aria-haspopup')) {
        el.setAttribute('aria-haspopup', 'listbox');
      }

      if (!el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', 'false');
      }

      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', 'Filter by routing type');
      }
    }

    // p-autocomplete panel
    if (el.classList.contains('p-autocomplete-panel')) {
      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      }

      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        el.setAttribute('aria-label', 'Autocomplete options');
      }

      const ul = el.querySelector('ul');
      if (ul) {
        if (!ul.hasAttribute('role')) {
          ul.setAttribute('role', 'listbox');
        }
        if (!ul.hasAttribute('tabindex')) {
          ul.setAttribute('tabindex', '0');
        }

        if (!ul.hasAttribute('aria-label') && !ul.hasAttribute('aria-labelledby')) {
          ul.setAttribute('aria-label', 'Selectable list of options');
        }

        ul.querySelectorAll('li').forEach((li) => {
          if (!li.hasAttribute('role') && !li.classList.contains('p-listbox-empty-message')) {
            li.setAttribute('role', 'option');
          }
        });
      }

      el.addEventListener(
        'keydown',
        (evt: KeyboardEvent) => {
          const key = evt.key;
          const step = 40;
          const page = el.clientHeight - 10;
          if (key === 'ArrowDown') {
            el.scrollTop += step;
            evt.preventDefault();
          } else if (key === 'ArrowUp') {
            el.scrollTop -= step;
            evt.preventDefault();
          } else if (key === 'PageDown') {
            el.scrollTop += page;
            evt.preventDefault();
          } else if (key === 'PageUp') {
            el.scrollTop -= page;
            evt.preventDefault();
          } else if (key === 'Home') {
            el.scrollTop = 0;
            evt.preventDefault();
          } else if (key === 'End') {
            el.scrollTop = el.scrollHeight;
            evt.preventDefault();
          }
        },
        { once: true }
      );
    }

    // p-calendar
    if (el.classList.contains('p-calendar')) {
      const input = el.querySelector('input[type="text"]');
      const popup = el.querySelector('.p-datepicker');

      if (input) {
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-haspopup', 'dialog');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-label', 'Date picker');
        input.setAttribute('placeholder', 'Select date');

        if (popup && !popup.id) {
          popup.id = 'calendar-panel-' + Math.random().toString(36).substring(2, 8);
        }

        if (popup?.id) {
          input.setAttribute('aria-controls', popup.id);
        }
      }
    }

    // p-datepicker (if used separately from p-calendar)
    if (el.classList.contains('p-datepicker')) {
      const input = el.querySelector('input[type="text"]');

      if (input) {
        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-haspopup', 'dialog');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-label', 'Date picker');
      }
    }

    // p-dropdown
    if (el.classList.contains('p-dropdown')) {
      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', 'dropdown menu');
      }
    }

    const roInput = el.querySelector('input[type="text"][readonly]');
    if (roInput) {
      if (!roInput.hasAttribute('role')) roInput.setAttribute('role', 'combobox');
      if (!roInput.hasAttribute('aria-haspopup')) roInput.setAttribute('aria-haspopup', 'listbox');
      if (!roInput.hasAttribute('aria-expanded')) roInput.setAttribute('aria-expanded', 'false');
    }

    // p-dropdown used in paginator
    if (el.classList.contains('p-dropdown') && el.classList.contains('p-paginator-rpp-options')) {
      const input = el.querySelector('input[type="text"]');
      const trigger = el.querySelector('.p-dropdown-trigger');
      const label = el.querySelector('.p-dropdown-label');

      if (input) {
        if (!input.hasAttribute('role')) {
          input.setAttribute('role', 'combobox');
        }

        if (!input.hasAttribute('aria-haspopup')) {
          input.setAttribute('aria-haspopup', 'listbox');
        }

        if (!input.hasAttribute('aria-expanded')) {
          input.setAttribute('aria-expanded', 'false');
        }

        if (!input.hasAttribute('aria-label')) {
          input.setAttribute('aria-label', 'Items per page selector');
        }

        if (!input.hasAttribute('disabled') && el.classList.contains('p-disabled')) {
          input.setAttribute('disabled', '');
        }
      }

      if (trigger && !trigger.hasAttribute('aria-label')) {
        trigger.setAttribute('aria-label', 'Open items per page dropdown');
      }

      if (label && !label.hasAttribute('aria-label')) {
        label.setAttribute('aria-label', 'Selected items per page');
      }
    }

    // p-paginator navigation buttons
    if (el.tagName === 'BUTTON' && el.classList.contains('p-paginator-element')) {
      const labelMap = {
        'p-paginator-first': 'First page',
        'p-paginator-prev': 'Previous page',
        'p-paginator-next': 'Next page',
        'p-paginator-last': 'Last page',
      };
      type LabelMapKey = keyof typeof labelMap;

      for (const cls in labelMap) {
        if (el.classList.contains(cls) && !el.hasAttribute('aria-label')) {
          el.setAttribute('aria-label', labelMap[cls as LabelMapKey]);
        }
      }

      if (!el.hasAttribute('role')) {
        el.setAttribute('role', 'button');
      }

      if (!el.hasAttribute('aria-disabled')) {
        const isDisabled = el.hasAttribute('disabled') || el.classList.contains('p-disabled');
        el.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
      }

      if (!el.hasAttribute('tabindex') && !el.hasAttribute('disabled')) {
        el.setAttribute('tabindex', '0');
      }
    }

    // readonly input[type="text"]
    if (el.tagName === 'INPUT' && el.getAttribute('type') === 'text' && el.getAttribute('readonly') === 'readonly') {
      el.setAttribute('role', 'combobox');

      if (!el.hasAttribute('aria-haspopup')) {
        el.setAttribute('aria-haspopup', 'listbox');
      }

      if (!el.hasAttribute('aria-expanded')) {
        el.setAttribute('aria-expanded', 'false');
      }

      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', 'Selection input');
      }

      if (!el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '0');
      }

      if (!el.hasAttribute('aria-controls')) {
        const isMultiSelect = !!el.closest('.p-multiselect');
        const panelSelector = isMultiSelect
          ? '.p-multiselect-panel ul[role="listbox"]'
          : '.p-dropdown-panel ul[role="listbox"], .p-autocomplete-panel ul[role="listbox"]';

        let listbox = this.document.querySelector(panelSelector) as HTMLElement;

        if (!listbox) {
          listbox = this.document.querySelector('ul[role="listbox"]') as HTMLElement;
        }

        if (listbox) {
          const listboxId = listbox.id || `listbox-${Math.random().toString(36).substring(2, 8)}`;
          listbox.id = listboxId;

          el.setAttribute('aria-controls', listboxId);
        } else {
          const fallbackId = `listbox-fallback-${el.id}`;
          el.setAttribute('aria-controls', fallbackId);
        }
      }
    }

    // p-table / p-datatable
    if (el.classList.contains('p-table') || el.classList.contains('p-datatable')) {
      const table = el.querySelector('table');

      if (table && !table.hasAttribute('role')) {
        table.setAttribute('role', 'table');
      }

      const headers = table?.querySelectorAll('th');
      headers?.forEach((th) => {
        if (!th.hasAttribute('scope')) {
          th.setAttribute('scope', 'col');
        }
      });
    }

    // p-inputnumber fix (spinbutton role and aria-valuenow="null" fix)
    if (el.classList.contains('p-inputnumber-input') && el.tagName === 'INPUT') {
      const inputElement = el as HTMLInputElement;

      if (!inputElement.hasAttribute('role')) {
        inputElement.setAttribute('role', 'spinbutton');
      }

      const val = inputElement.getAttribute('aria-valuenow');

      if (val === 'null' || val === '') {
        inputElement.setAttribute('aria-valuenow', inputElement.value || '0');
      }
    }

    // p-multiselect listbox
    if (el.tagName === 'UL' && el.getAttribute('role') === 'listbox') {
      if (!el.hasAttribute('aria-label') && !el.hasAttribute('aria-labelledby')) {
        el.setAttribute('aria-label', 'Available options'); // ✅ meaningful name
      }
    }

    if (el.tagName === 'LI' && el.closest('ul[role="listbox"]')) {
      const li = el as HTMLElement;

      if (li.classList.contains('p-multiselect-item') && !li.hasAttribute('role')) {
        li.setAttribute('role', 'option');
      }
      if (li.classList.contains('p-multiselect-option-group')) {
        if (!li.hasAttribute('role')) li.setAttribute('role', 'option');
      }

      if ((li.getAttribute('role') || '').toLowerCase() === 'option') {
        const visibleText = (li.innerText || li.textContent || '').trim();
        const ariaLabel = (li.getAttribute('aria-label') || '').trim();
        const labelledBy = (li.getAttribute('aria-labelledby') || '').trim();

        if (visibleText) {
          if (!labelledBy) {
            if (!ariaLabel || !ariaLabel.toLowerCase().includes(visibleText.toLowerCase())) {
              li.setAttribute('aria-label', visibleText);
            }
          }
        } else {
          if (!ariaLabel && !labelledBy) {
            li.setAttribute('aria-label', 'Option');
          }
        }
      }
    }

    // p-tabpanel
    if (el.tagName === 'DL') {
      const childDLs = el.querySelectorAll(':scope > dl');
      childDLs.forEach((child) => {
        const wrapperDD = document.createElement('dd');
        el.insertBefore(wrapperDD, child);
        wrapperDD.appendChild(child);
      });
    }

    // p-overlayPanel Dialog + p-confirmDialog Alert Dialog fix
    if (el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog') {
      if (!el.hasAttribute('aria-modal')) {
        el.setAttribute('aria-modal', 'true');
      }

      const hasLabel = el.hasAttribute('aria-label');
      const hasLabelledBy = el.hasAttribute('aria-labelledby');

      if (!hasLabel && !hasLabelledBy) {
        const heading = el.querySelector('h1, h2, h3, .p-dialog-title');

        if (heading) {
          const headingId = heading.id || `dialog-heading-${Math.random().toString(36).substring(2, 8)}`;
          heading.id = headingId;
          el.setAttribute('aria-labelledby', headingId);
        } else {
          el.setAttribute('aria-label', 'Confirmation Dialog');
        }
      }
    }

    if (el.tagName.toLowerCase() === 'p-dialog') {
      const hasLabel = el.hasAttribute('aria-label');
      const hasLabelledBy = el.hasAttribute('aria-labelledby');
      const hasTitle = el.hasAttribute('title');

      if (!hasLabel && !hasLabelledBy && !hasTitle) {
        const heading = el.querySelector('h1, h2, h3, .p-dialog-title');

        if (heading) {
          if (!heading.id) {
            heading.id = 'dlg-' + Math.random().toString(36).substring(2, 8);
          }
          el.setAttribute('aria-labelledby', heading.id);
        } else {
          el.setAttribute('aria-label', 'Dialog');
        }
      }
    }

    if (
      el.matches('span.p-hidden-accessible.p-hidden-focusable') ||
      el.getAttribute('data-pc-section') === 'firsthiddenfocusableel'
    ) {
      if (el.hasAttribute('tabindex')) {
        el.setAttribute('tabindex', '-1');
      }
    }

    if (el.tagName === 'INPUT' && el.classList.contains('p-checkbox-input') && el.closest('li[role="option"]')) {
      const input = el as HTMLInputElement;

      input.removeAttribute('aria-label');
      input.removeAttribute('aria-labelledby');
      input.removeAttribute('title');
      input.removeAttribute('placeholder');

      input.removeAttribute('name');
      input.removeAttribute('value');
      input.removeAttribute('checked');
      input.removeAttribute('aria-checked');

      input.setAttribute('role', 'presentation');
      input.setAttribute('aria-hidden', 'true');

      input.setAttribute('tabindex', '-1');

      const pchk = input.closest('p-checkbox');
      if (pchk) {
        pchk.setAttribute('role', 'presentation');
        pchk.setAttribute('aria-hidden', 'true');
        pchk.removeAttribute('aria-label');
        pchk.removeAttribute('aria-labelledby');
        pchk.removeAttribute('title');
      }
    }

    if (el.tagName === 'LI' && el.getAttribute('role') === 'option') {
      const visibleText = (el.innerText || '').trim();

      if (visibleText) {
        let lbl = this.document.getElementById(el.id + '-lbl');
        if (!lbl) {
          lbl = this.document.createElement('span');
          lbl.id = el.id + '-lbl';
          lbl.textContent = visibleText;

          lbl.setAttribute('hidden', 'true');

          this.document.body.appendChild(lbl);
        }

        el.setAttribute('aria-labelledby', lbl.id);

        el.removeAttribute('aria-label');
      }
    }

    if (el.tagName === 'LI' && (el.getAttribute('role') || '').toLowerCase() === 'option') {
      const li = el as HTMLElement;

      const textContainerSelectors = [
        '[data-pc-section="label"]',
        '.p-checkbox-label',
        '.p-multiselect-item-label',
        'span',
        'div',
      ];

      let labelEl: HTMLElement | null = null;
      for (const sel of textContainerSelectors) {
        const candidate = li.querySelector(sel) as HTMLElement | null;
        if (candidate) {
          const t = (candidate.innerText || '').trim();
          if (t && candidate.getAttribute('aria-hidden') !== 'true') {
            labelEl = candidate;
            break;
          }
        }
      }

      if (labelEl) {
        if (!labelEl.id) {
          labelEl.id = li.id ? `${li.id}-label` : `optlbl-${Math.random().toString(36).slice(2, 8)}`;
        }

        li.setAttribute('aria-labelledby', labelEl.id);

        li.removeAttribute('aria-label');
      } else {
        const fallback = (li.getAttribute('aria-label') || '').trim() || 'Option';
        li.setAttribute('aria-label', fallback);
        li.removeAttribute('aria-labelledby');
      }
    }

    if (
      el.tagName.toLowerCase() === 'p-dialog' &&
      (el.getAttribute('role') === 'dialog' || el.getAttribute('role') === 'alertdialog')
    ) {
      const hasLabel = el.hasAttribute('aria-label') && (el.getAttribute('aria-label') || '').trim() !== '';
      const hasLabelledBy =
        el.hasAttribute('aria-labelledby') && (el.getAttribute('aria-labelledby') || '').trim() !== '';
      const hasTitle = el.hasAttribute('title') && (el.getAttribute('title') || '').trim() !== '';

      if (!hasLabel && !hasLabelledBy && !hasTitle) {
        const heading = el.querySelector('h1, h2, h3, .p-dialog-title') as HTMLElement | null;

        if (heading) {
          if (!heading.id) heading.id = 'dlg-' + Math.random().toString(36).substring(2, 8);
          el.setAttribute('aria-labelledby', heading.id);
        } else {
          el.setAttribute('aria-label', 'Dialog');
        }
      }
    }

    const isPickerNavBtn =
      el.tagName === 'BUTTON' &&
      (el.classList.contains('p-datepicker-select-month') || el.classList.contains('p-datepicker-select-year'));

    if (isPickerNavBtn) {
      const btn = el as HTMLButtonElement;

      const visibleText = (btn.innerText || '').trim();

      if (visibleText) {
        const textNode = btn.querySelector(
          '[data-pc-section="selectmonth"], [data-pc-section="selectyear"], span, div'
        ) as HTMLElement | null;

        if (textNode && (textNode.innerText || '').trim()) {
          if (!textNode.id) {
            textNode.id = 'dp-' + Math.random().toString(36).substring(2, 8);
          }
          btn.setAttribute('aria-labelledby', textNode.id);
          btn.removeAttribute('aria-label');
        } else {
          const current = (btn.getAttribute('aria-label') || '').trim();
          if (current.toLowerCase() !== visibleText.toLowerCase()) {
            btn.setAttribute('aria-label', visibleText);
          }
        }
      } else {
        if (!btn.hasAttribute('aria-label') && !btn.hasAttribute('aria-labelledby')) {
          btn.setAttribute('aria-label', 'Select month');
        }
      }
    }

    if (el.getAttribute('role') === 'tabpanel') {
      const rawId = el.id || '';
      if (!rawId) return;

      if (/\s/.test(rawId)) {
        const safeId = rawId
          .trim()
          .replace(/\s+/g, '-')
          .replace(/[^\w\-.]/g, '');

        el.id = safeId;

        const tabSelector = `[aria-controls="${rawId}"]`;
        const tab = this.document.querySelector(tabSelector) as HTMLElement | null;
        if (tab) {
          tab.setAttribute('aria-controls', safeId);
        }
      }
    }

    const inDatepicker = !!el.closest?.('.p-datepicker, [data-pc-section="panel"], [role="dialog"] .p-datepicker');
    const isDaySpan = el.tagName === 'SPAN' && el.classList.contains('p-datepicker-day');
    const isDayCell = el.tagName === 'TD' && el.classList.contains('p-datepicker-day-cell');

    if (inDatepicker && (isDaySpan || isDayCell)) {
      const dayEl = el as HTMLElement;

      const tabAttr = dayEl.getAttribute('tabindex');
      const isTabbable = tabAttr === '0' || tabAttr === '1' || tabAttr === '' || dayEl === this.document.activeElement;

      if (isTabbable) {
        if (!dayEl.hasAttribute('role')) {
          dayEl.setAttribute('role', 'button');
        }
      }

      const iso = (dayEl.getAttribute('data-date') || '').trim();
      let label = (dayEl.innerText || '').trim();
      if (iso) {
        const parts = iso.split('-').map((n) => parseInt(n, 10));
        if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
          const [y, m, d] = parts;
          const months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
          ];
          label = `${d} ${months[Math.max(0, Math.min(11, m - 1))]} ${y}`;
        }
      }
      if (label) {
        const current = (dayEl.getAttribute('aria-label') || '').trim();
        if (!current || current.toLowerCase() !== label.toLowerCase()) {
          dayEl.setAttribute('aria-label', label);
        }
      }

      if (dayEl.classList.contains('p-datepicker-today') && dayEl.getAttribute('aria-current') !== 'date') {
        dayEl.setAttribute('aria-current', 'date');
      }

      if (dayEl.classList.contains('p-disabled') || dayEl.getAttribute('data-p-disabled') === 'true') {
        dayEl.setAttribute('aria-disabled', 'true');
      }
    }

    if (el.tagName === 'TD' && el.classList.contains('p-datepicker-day-cell')) {
      const cell = el as HTMLElement;

      const dayNode = cell.querySelector('[data-pc-section="day"], .p-datepicker-day, span, div') as HTMLElement | null;

      const visibleText = (dayNode?.innerText || cell.innerText || '').trim();

      if (visibleText) {
        if (dayNode) {
          if (!dayNode.id) {
            dayNode.id = 'dp-day-' + Math.random().toString(36).substring(2, 8);
          }
          cell.setAttribute('aria-labelledby', dayNode.id);

          cell.removeAttribute('aria-label');
        } else {
          const current = (cell.getAttribute('aria-label') || '').trim();
          if (!current || current.toLowerCase() !== visibleText.toLowerCase()) {
            cell.setAttribute('aria-label', visibleText);
          }
          cell.removeAttribute('aria-labelledby');
        }
      } else {
        if (!cell.hasAttribute('aria-label') && !cell.hasAttribute('aria-labelledby')) {
          cell.setAttribute('aria-label', 'Day');
        }
      }
    }

    if (
      el.tagName === 'LI' &&
      (el.getAttribute('role') || '').toLowerCase() === 'option' &&
      el.closest('ul[role="listbox"]')
    ) {
      const li = el as HTMLElement;

      let labelElement: HTMLElement | null = null;
      let textFromDirectNode = '';

      const explicit = li.querySelector(
        '[data-pc-section="label"], .p-multiselect-item-label, .p-checkbox-label'
      ) as HTMLElement | null;
      if (explicit && (explicit.innerText || '').trim()) {
        labelElement = explicit;
      } else {
        for (const node of Array.from(li.childNodes)) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const elNode = node as HTMLElement;
            if (elNode.matches('p-checkbox,[data-pc-name="pcoptioncheckbox"],.p-checkbox')) continue;
          }
          if (node.nodeType === Node.TEXT_NODE) {
            const s = (node.textContent || '').trim();
            if (s) {
              textFromDirectNode = s;
              break;
            }
          }
        }
      }

      let labelId = '';
      if (labelElement) {
        if (!labelElement.id) labelElement.id = `${li.id || 'opt'}-label`;
        labelId = labelElement.id;
      } else if (textFromDirectNode) {
        const hiddenId = `${li.id || 'opt'}-lbl-copy`;
        let hidden = this.document.getElementById(hiddenId) as HTMLElement | null;
        if (!hidden) {
          hidden = this.document.createElement('span');
          hidden.id = hiddenId;
          hidden.textContent = textFromDirectNode;
          hidden.setAttribute('hidden', 'true');
          this.document.body.appendChild(hidden);
        }
        labelId = hidden.id;
      }

      if (labelId) {
        li.setAttribute('aria-labelledby', labelId);
        li.removeAttribute('aria-label');
      } else {
        const visible = (li.innerText || '').trim();
        if (visible) {
          li.setAttribute('aria-label', visible);
          li.removeAttribute('aria-labelledby');
        }
      }
    }

    if (
      el.tagName === 'LI' &&
      (el.getAttribute('role') || '').toLowerCase() === 'option' &&
      el.closest('ul[role="listbox"]')
    ) {
      const li = el as HTMLElement;

      const pchk = li.querySelector('p-checkbox,[data-pc-name="pcoptioncheckbox"],.p-checkbox') as HTMLElement | null;
      if (pchk) {
        pchk.setAttribute('role', 'presentation');
        pchk.setAttribute('aria-hidden', 'true');
      }

      const input = li.querySelector('input.p-checkbox-input') as HTMLInputElement | null;
      if (input) {
        input.setAttribute('tabindex', '-1');
        input.setAttribute('role', 'presentation');
        input.setAttribute('aria-hidden', 'true');
        input.removeAttribute('aria-label');
        input.removeAttribute('aria-labelledby');
        input.removeAttribute('title');
      }
    }
  }

  // Fix PrimeNG dropdown ARIA roles
  private fixDropdownRoles(element: HTMLElement): void {
    const wrappers = element.querySelectorAll('.p-dropdown-items-wrapper');

    wrappers.forEach((wrapper) => {
      if (wrapper.getAttribute('role') === 'listbox') {
        wrapper.removeAttribute('role');
        wrapper.setAttribute('role', 'presentation');
      }

      if (!wrapper.hasAttribute('tabindex')) {
        wrapper.setAttribute('tabindex', '0');
      }

      const ul = wrapper.querySelector('ul');

      if (ul && !ul.hasAttribute('role')) {
        ul.setAttribute('role', 'listbox');
      }

      const liElements = ul?.querySelectorAll('li') || [];
      liElements.forEach((li) => {
        if (!li.hasAttribute('role')) {
          li.setAttribute('role', 'option');
        }
      });
    });
  }

  private fixMissingLandmarks(el: HTMLElement): void {
    let label: string | null = null;
    let target: HTMLElement | null = el;

    if (el.matches('.p-multiselect-items-wrapper')) {
      label = 'Multiselect Options';
    } else if (el.matches('input.p-multiselect-filter')) {
      label = el.getAttribute('placeholder') || el.getAttribute('title') || 'Search Filter';
    } else if (el.classList.contains('p-checkbox')) {
      label = el.getAttribute('aria-label') || 'Checkbox option';
    } else if (el.classList.contains('p-overlay')) {
      label = 'Overlay Panel';
    } else if (el.classList.contains('p-datepicker-calendar-container')) {
      label = 'Date Picker Calendar';
    }

    if (el.matches('h5.mt-4.font-semibold') && !el.closest('main,[role="main"],section[role="region"],header,footer')) {
      const parent = el.parentElement;
      if (parent && !parent.hasAttribute('role')) {
        parent.setAttribute('role', 'region');
      }

      if (parent && !parent.hasAttribute('aria-label')) {
        parent.setAttribute('aria-label', 'Recent Authorizations');
      }
      return;
    }

    if (
      el.closest(
        'main,[role="main"],section[role="region"],header,footer,nav,[role="navigation"],aside,[role="complementary"]'
      )
    ) {
      return;
    }

    if (el.matches('.p-multiselect-items-wrapper') || el.matches('input.p-multiselect-filter')) {
      const parentComposite = el.closest('.p-multiselect') as HTMLElement | null;
      if (parentComposite) target = parentComposite;
    }

    if (el.classList.contains('p-overlay')) {
      target = el;
    }

    if (el.classList.contains('p-datepicker-calendar-container')) {
      const parentPanel = el.closest('.p-datepicker') as HTMLElement | null;
      target = parentPanel ?? el;
    }

    if (label && target) {
      if (!target.hasAttribute('role')) {
        target.setAttribute('role', 'region');
      }
      if (!target.hasAttribute('aria-label') && !target.hasAttribute('aria-labelledby')) {
        target.setAttribute('aria-label', `${label} ${++this.landmarkCounter}`);
      }
    }
  }

  //PrimeNG accessible name fix
  private fixAccessibleNames(element: HTMLElement): void {
    const listboxes = element.querySelectorAll('[role="listbox"]');

    listboxes.forEach((listbox) => {
      const hasAriaLabel = listbox.hasAttribute('aria-label') && listbox.getAttribute('aria-label')?.trim() !== '';
      const hasAriaLabelledBy =
        listbox.hasAttribute('aria-labelledby') && listbox.getAttribute('aria-labelledby')?.trim() !== '';

      if (!hasAriaLabel && !hasAriaLabelledBy) {
        listbox.setAttribute('aria-label', 'Selectable list of options');
      }

      const liItems = listbox.querySelectorAll('li');
      liItems.forEach((li) => {
        if (!li.classList.contains('p-listbox-empty-message') && !li.classList.contains('p-multiselect-item-group')) {
          li.setAttribute('role', 'option');
        }

        if (li.classList.contains('p-multiselect-item-group') && li.getAttribute('role') !== 'presentation') {
          li.setAttribute('role', 'presentation');
        }
      });
    });
  }

  //Fix for interactive elements (buttons, spans with role="button", links) to have an accessible name.
  private fixUnlabeledInteractiveElements(element: HTMLElement): void {
    const interactiveElements = element.querySelectorAll(
      'button, [role="button"], a[role="button"], span[role="button"]'
    );

    interactiveElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      const hasText = htmlEl.textContent?.trim() !== '';
      const hasAriaLabel = htmlEl.hasAttribute('aria-label') && htmlEl.getAttribute('aria-label')?.trim() !== '';
      const hasAriaLabelledBy =
        htmlEl.hasAttribute('aria-labelledby') && htmlEl.getAttribute('aria-labelledby')?.trim() !== '';
      const hasTitle = htmlEl.hasAttribute('title') && htmlEl.getAttribute('title')?.trim() !== '';

      if (!hasText && !hasAriaLabel && !hasAriaLabelledBy && !hasTitle) {
        htmlEl.setAttribute('aria-label', 'action');
      }
    });
  }

  //Generic input fixes
  private fixGenericInputs(el: HTMLElement): void {
    if (el.tagName === 'INPUT') {
      const name = el.getAttribute('formcontrolname') || el.getAttribute('name') || 'input field';

      if (!el.hasAttribute('aria-label')) {
        el.setAttribute('aria-label', name);
      }

      if (!el.hasAttribute('placeholder')) {
        el.setAttribute('placeholder', `Enter ${name}`);
      }

      const id = el.id || `input-${Math.random().toString(36).substring(2, 8)}`;
      el.id = id;
      const label = el.closest('.field')?.querySelector('label');

      if (label) {
        if (!label.hasAttribute('for')) {
          label.setAttribute('for', id);
        }

        if (!label.id) label.id = `lbl-${id}`;
        el.setAttribute('aria-labelledby', label.id);
      }
    }
  }

  private fixInvalidAriaValues(el: HTMLElement): void {
    if (el.hasAttribute('aria-valuenow')) {
      const role = el.getAttribute('role') || '';
      const allowedRoles = ['spinbutton', 'slider', 'progressbar'];

      if (!allowedRoles.includes(role)) {
        el.removeAttribute('aria-valuenow');
      } else {
        const val = el.getAttribute('aria-valuenow');

        if (val === 'null' || val === '') {
          const currentValue = (el as HTMLInputElement).value || '0';
          el.setAttribute('aria-valuenow', currentValue);
        }
      }
    }
  }

  //Fixes invalid ARIA `aria-activedescendant` attribute values
  private fixInvalidAriaActivedescendant(el: HTMLElement): void {
    if (el.hasAttribute('aria-activedescendant')) {
      const targetId = el.getAttribute('aria-activedescendant')!;

      if (!this.document.getElementById(targetId)) {
        el.removeAttribute('aria-activedescendant');
      }
    }
  }

  private fixInvalidAriaMultiselectable(el: HTMLElement): void {
    if (el.hasAttribute('aria-multiselectable')) {
      const value = el.getAttribute('aria-multiselectable')!;

      if (value !== 'true' && value !== 'false') {
        el.setAttribute('aria-multiselectable', 'true');
      }
    }
  }

  private fixNestedInteractiveControls(element: HTMLElement): void {
    element.querySelectorAll('a[role="button"]').forEach((anchor) => {
      if (anchor.querySelector('button')) {
        anchor.removeAttribute('role');
        anchor.removeAttribute('tabindex');
        anchor.removeAttribute('aria-expanded');
        anchor.removeAttribute('aria-controls');
      }
    });
  }

  // Fix WCAG issue: Ensure <th> elements have discernible text + fix for empty table headers.
  private fixEmptyOrGenericTableHeaders(element: HTMLElement): void {
    const headers = element.querySelectorAll('th');
    headers.forEach((th, index) => {
      const text = (th.textContent || '').trim();
      const ariaLabel = (th.getAttribute('aria-label') || '').trim();

      if (!th.hasAttribute('scope')) {
        th.setAttribute('scope', 'col');
      }

      if (!text) {
        const name = ariaLabel || th.getAttribute('data-field') || `Column ${index + 1}`;

        const sr = this.document.createElement('span');
        sr.setAttribute(
          'style',
          [
            'border:0',
            'clip:rect(0 0 0 0)',
            'clip-path:inset(50%)',
            'height:1px',
            'margin:-1px',
            'overflow:hidden',
            'padding:0',
            'position:absolute',
            'white-space:nowrap',
            'width:1px',
          ].join(';')
        );
        sr.textContent = name;

        th.appendChild(sr);

        th.setAttribute('aria-label', name);
      }
    });
  }

  private fixInvalidAriaRequired(el: HTMLElement): void {
    if (el.hasAttribute('aria-required')) {
      const role = el.getAttribute('role') || '';
      const allowedRoles = ['radiogroup', 'combobox', 'textbox', 'listbox'];

      if (!allowedRoles.includes(role)) {
        const hasRadioButtons = el.querySelector('input[type="radio"]') !== null;

        if (hasRadioButtons) {
          el.setAttribute('role', 'radiogroup');
        } else {
          el.removeAttribute('aria-required');
        }
      }
    }
  }
}
